import { FieldWithLabel } from 'components/FieldWithLabel/FieldWithLabel'
import { BTN_TXT, LABELS } from 'constants/txt'
import { useBankAccountDetailsContext } from './Providers/BankAccountDetailsContextProvider'
import './styles.scss'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { formatBankAccounName } from '../helpers'
import { Container } from 'components/Container/Container'
import {
  BANK_ACCOUNT_STATUS,
  BREADCRUMBS
} from '../BankAccountsTable/constants'
import { RowLayout } from 'features/MyCommunity/Vendors/VendorCreation/General/VendorForm/RowLayout'
import Input from 'components/Input/Input'
import { TBankAccountForm, TDefaultReceivableAccountAction } from '../types'
import { CustomForm } from 'components/Forms/CustomForm'
import { useBankAccountDetailsForm } from './hooks/useBankAccountDetailsForm'
import { validateMessages } from 'helper/common'
import { FloatFooter } from 'components/FloatFooter/FloatFooter'
import { BankAccountDetailsStatus } from './BankAccountDetailsStatus'
import Button from 'components/Button/Button'
import { BUTTON_TYPES } from 'components/Button'
import { BankAccountDetailsBanner } from './BankAccountDetailsBanner'
import { DeleteBankAccount } from '../DeleteBankAccount'
import { useBankAccountPopup } from '../Providers/BankAccountPopupProvider'
import { useNavigate, useParams } from 'react-router-dom'
import { routes } from 'router/Config/config.routes'
import { VerifyDeposit } from '../VerifyDeposit'
import { ReactComponent as EventSchedule16 } from 'assets/svg/EventSchedule16.svg'
import { defaultDateFormat, defaultTimeFormat } from 'helper/dates'
import { Form, Radio } from 'antd'
import { Typography } from 'components/Typography'
import {
  ACCOUNT_PURPOSE_OPTIONS,
  ACCOUNT_PURPOSE_OPTIONS_DESCRIPTIONS,
  ACCOUNT_PURPOSE_OPTIONS_FULL_LABELS
} from './constants'
import cn from 'classnames'
import { Separator } from 'components/Separator/Separator'
import { useSelector } from 'react-redux'
import { getUser } from 'redux/store/user/getters'
import { ROLES } from 'features/Permission'
import { SetAsDefaultReceivableBankAccount } from '../SetAsDefaultReceivableBankAccount'
import { CantProceedWithDefaultReceivableAccount } from '../CantProceedWithDefaultReceivableAccount'
import { getListOfAvailableAccountsToSetAsDefault } from '../api'
import { useAppDispatch } from 'redux/createStore'
import { setLoading } from 'redux/store/common/slice'
import { ThisAccountIsDefaultReceivableConfirmation } from '../ThisAccountIsDefaultReceivableConfirmation'
import { SetNewDefaultReceivableAccount } from '../SetNewDefaultReceivableAccount'
import { Chips } from 'components/Chips/Chips'

export const BankAccountDetails = () => {
  const {
    state: {
      details,
      moovBusinessAccountId,
      entityName,
      canSendDeposit,
      canVerifyDeposit,
      canResendDeposit,
      accountPurposeOptions,
      canChangeAccountPurpose,
      canEditBankAccountDetails,
      canSetAsDefaultReceivableAccount
    },
    actions: { onSendDeposit, getDetails }
  } = useBankAccountDetailsContext()

  const dispatch = useAppDispatch()

  const {
    form,
    errors,
    isActive,
    setIsActive,
    setErrors,
    onFormChange,
    onFinish
  } = useBankAccountDetailsForm()

  const {
    deleteBankAccountPopup,
    verifyDepositsPopup,
    setAsDefaultReceivableAccountPopup,
    cantProceedWithDefaultReceivableAccountPopup,
    thisAccountIsDefaultReceivableAccountPopup,
    setAnotherDefaultReceivableAccountPopup
  } = useBankAccountPopup()
  const navigate = useNavigate()
  const { hsId, hospitalId } = useParams()

  const user = useSelector(getUser)

  const isEditMode = canEditBankAccountDetails

  const bankAccountName = useMemo(
    () => formatBankAccounName(details),
    [details]
  )

  const initialValues = useMemo(() => {
    return {
      nickname: details?.nickname,
      purpose: details?.purpose
    }
  }, [details?.nickname, details?.purpose])

  const predefineFormData = useCallback(() => {
    if (details) {
      form.setFieldsValue(initialValues)
    }
  }, [details, form, initialValues])

  const onCancel = useCallback(() => {
    setIsActive(false)
    if (details) {
      predefineFormData()
    } else {
      form.resetFields()
    }
    setErrors({})
  }, [setIsActive, details, setErrors, predefineFormData, form])

  const onDeleteCallback = useCallback(() => {
    if (hsId && hospitalId) {
      navigate(
        `${routes.myCommunity}${routes.myOrganization}/health_systems/${hsId}/hospitals/${hospitalId}/bank_accounts`
      )
      return
    } else if (hsId) {
      navigate(
        `${routes.myCommunity}${routes.myOrganization}/health_systems/${hsId}/bank_accounts`
      )
      return
    }
    navigate(routes.myCommunity + routes.myOrganization + routes.bankAccounts)
  }, [navigate, hsId, hospitalId])

  const onVerify = useCallback(() => {
    getDetails()
  }, [getDetails])

  const onSetAsDefaultReceivableAccountCallback = useCallback(() => {
    getDetails()
  }, [getDetails])

  const allowLeaveWithoutDiscardRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    if (isActive) return
    allowLeaveWithoutDiscardRef.current?.()
    allowLeaveWithoutDiscardRef.current = null
  }, [isActive])

  const waitUntilLeaveIsAllowed = useCallback(() => {
    if (!isActive) return Promise.resolve()
    return new Promise<void>((resolve) => {
      allowLeaveWithoutDiscardRef.current = resolve
      setIsActive(false)
    })
  }, [isActive, setIsActive])

  const openDefaultReceivableReplacementFlow = useCallback(
    async ({
      action,
      formData,
      onSuccess
    }: {
      action: TDefaultReceivableAccountAction
      formData?: Partial<TBankAccountForm>
      onSuccess?: () => void
    }) => {
      if (!details?.uuid || !moovBusinessAccountId) return
      try {
        dispatch(setLoading(true))
        const {
          data: { results: listOfAvailableAccountsToSetAsDefault }
        } = await getListOfAvailableAccountsToSetAsDefault(
          moovBusinessAccountId,
          details.uuid
        )
        if (listOfAvailableAccountsToSetAsDefault.length > 0) {
          thisAccountIsDefaultReceivableAccountPopup.actions.open({
            name: formatBankAccounName(details),
            action,
            callback: () => {
              thisAccountIsDefaultReceivableAccountPopup.actions.close()
              setAnotherDefaultReceivableAccountPopup.actions.open({
                name: formatBankAccounName(details),
                uuid: details.uuid,
                business_account_id: moovBusinessAccountId,
                listOfAvailableAccountsToSetAsDefault,
                action,
                formData,
                onSuccess
              })
            }
          })
        } else {
          cantProceedWithDefaultReceivableAccountPopup.actions.open({
            name: formatBankAccounName(details),
            action,
            onAddBankAccount: waitUntilLeaveIsAllowed
          })
        }
      } finally {
        dispatch(setLoading(false))
      }
    },
    [
      cantProceedWithDefaultReceivableAccountPopup.actions,
      details,
      dispatch,
      moovBusinessAccountId,
      setAnotherDefaultReceivableAccountPopup.actions,
      thisAccountIsDefaultReceivableAccountPopup.actions,
      waitUntilLeaveIsAllowed
    ]
  )

  const onOpenDeleteBankAccountPopup = useCallback(async () => {
    if (!details?.uuid || !moovBusinessAccountId) return
    if (details?.is_default) {
      await openDefaultReceivableReplacementFlow({ action: 'delete' })
    } else {
      deleteBankAccountPopup.actions.open({
        uuid: details.uuid,
        name: formatBankAccounName(details),
        business_account_id: moovBusinessAccountId
      })
    }
  }, [
    deleteBankAccountPopup.actions,
    details,
    moovBusinessAccountId,
    openDefaultReceivableReplacementFlow
  ])

  const handleFinish = useCallback(async () => {
    const data = form.getFieldsValue()
    const isChangingDefaultReceivablePurpose =
      !!details?.is_default &&
      details?.purpose === ACCOUNT_PURPOSE_OPTIONS.RECEIVABLE &&
      data.purpose === ACCOUNT_PURPOSE_OPTIONS.PAYABLE

    if (isChangingDefaultReceivablePurpose) {
      await openDefaultReceivableReplacementFlow({
        action: 'changePurpose',
        formData: {
          nickname: data.nickname,
          purpose: data.purpose
        },
        onSuccess: () => {
          setIsActive(false)
          setErrors({})
          getDetails()
        }
      })
      return
    }

    await onFinish()
  }, [
    details?.is_default,
    details?.purpose,
    form,
    getDetails,
    onFinish,
    openDefaultReceivableReplacementFlow,
    setErrors,
    setIsActive
  ])

  const onOpenSetAsDefaultReceivableAccountPopup = useCallback(() => {
    if (!details?.uuid || !moovBusinessAccountId) return
    setAsDefaultReceivableAccountPopup.actions.open({
      uuid: details?.uuid,
      name: formatBankAccounName(details),
      business_account_id: moovBusinessAccountId
    })
  }, [
    setAsDefaultReceivableAccountPopup.actions,
    details,
    moovBusinessAccountId
  ])

  const onOpenVerifyDepositPopup = useCallback(() => {
    if (!details?.uuid || !moovBusinessAccountId) return
    verifyDepositsPopup.actions.open({
      uuid: details?.uuid,
      name: details.nickname,
      business_account_id: moovBusinessAccountId,
      account_number: details?.account_number
    })
  }, [
    verifyDepositsPopup.actions,
    details?.uuid,
    details?.nickname,
    details?.account_number,
    moovBusinessAccountId
  ])

  const renderBankAccountStatusComponent = useMemo(() => {
    return <BankAccountDetailsStatus status={details?.status} />
  }, [details?.status])

  const renderAccountNicknameComponent = useMemo(() => {
    return isEditMode ? (
      <Input
        propsItem={{
          name: 'nickname',
          label: LABELS.ACCOUNT_NICKNAME,
          help: errors.nickname?.[0],
          validateStatus: errors.nickname && 'error'
        }}
        propsInput={{
          maxLength: 100
        }}
      />
    ) : (
      <FieldWithLabel title={LABELS.ACCOUNT_NICKNAME}>
        {details?.nickname}
      </FieldWithLabel>
    )
  }, [isEditMode, errors.nickname, details?.nickname])

  const hideHintText = useMemo(() => {
    return canChangeAccountPurpose || user.role === ROLES.VENDOR_CONTRACT_ADMIN
  }, [canChangeAccountPurpose, user.role])

  const renderAccountPurposeBlock = useMemo(() => {
    return (
      <RowLayout title="Account purpose">
        <FieldWithLabel
          title={
            hideHintText
              ? ''
              : 'You can change the account purpose once verification is complete.'
          }
        >
          <Form.Item name="purpose">
            <Radio.Group size="large">
              <div className="column gap-8 flex">
                {accountPurposeOptions.map((option) => {
                  return (
                    <div
                      className={cn(
                        'account-purpose-option-wrapper row justify-between align-center mt-0',
                        {
                          disabled: !canChangeAccountPurpose
                        }
                      )}
                    >
                      <Radio
                        disabled={!canChangeAccountPurpose}
                        value={option.value}
                      >
                        <div className="account-purpose-option-label row justify-between">
                          <div className="column gap-4 column gap-4">
                            <Typography.Body1>
                              {
                                ACCOUNT_PURPOSE_OPTIONS_FULL_LABELS[
                                  option.value
                                ]
                              }
                            </Typography.Body1>
                            <Typography.Caption className="account-purpose-option-label__description">
                              {
                                ACCOUNT_PURPOSE_OPTIONS_DESCRIPTIONS[
                                  option.value
                                ]
                              }
                            </Typography.Caption>
                          </div>
                        </div>
                      </Radio>
                      {option.value === ACCOUNT_PURPOSE_OPTIONS.RECEIVABLE &&
                        details?.is_default && <Chips grey>Default</Chips>}
                      {option.value === ACCOUNT_PURPOSE_OPTIONS.RECEIVABLE &&
                        canSetAsDefaultReceivableAccount && (
                          <div className="column gap-4">
                            <Button
                              type={BUTTON_TYPES.LINK}
                              upperCase
                              disabled={!canChangeAccountPurpose}
                              onClick={onOpenSetAsDefaultReceivableAccountPopup}
                            >
                              {BTN_TXT.SET_AS_DEFAULT}
                            </Button>
                          </div>
                        )}
                    </div>
                  )
                })}
              </div>
            </Radio.Group>
          </Form.Item>
        </FieldWithLabel>
      </RowLayout>
    )
  }, [
    accountPurposeOptions,
    canChangeAccountPurpose,
    canSetAsDefaultReceivableAccount,
    details?.is_default,
    hideHintText,
    onOpenSetAsDefaultReceivableAccountPopup
  ])

  const actions = useMemo(() => {
    return (
      <>
        <Button
          type={BUTTON_TYPES.DEFAULT}
          upperCase
          danger
          onClick={onOpenDeleteBankAccountPopup}
        >
          {BTN_TXT.DELETE}
        </Button>
        {canSendDeposit && (
          <Button type={BUTTON_TYPES.PRIMARY} upperCase onClick={onSendDeposit}>
            {BTN_TXT.SEND_DEPOSITS}
          </Button>
        )}
        {canVerifyDeposit && (
          <Button
            type={BUTTON_TYPES.PRIMARY}
            upperCase
            onClick={onOpenVerifyDepositPopup}
          >
            {BTN_TXT.VERIFY_DEPOSITS}
          </Button>
        )}
        {canResendDeposit && (
          <Button type={BUTTON_TYPES.PRIMARY} upperCase onClick={onSendDeposit}>
            {BTN_TXT.RE_TRIGGER_SENDING_DEPOSITS}
          </Button>
        )}
      </>
    )
  }, [
    canSendDeposit,
    canVerifyDeposit,
    canResendDeposit,
    onSendDeposit,
    onOpenDeleteBankAccountPopup,
    onOpenVerifyDepositPopup
  ])

  const beforeStatusBlock = useMemo(() => {
    if (details?.status === BANK_ACCOUNT_STATUS.PENDING)
      return {
        text: `Micro deposits sent at ${defaultDateFormat(
          details?.micro_deposits_sent_at
        )} at ${defaultTimeFormat(details?.micro_deposits_sent_at)}`,
        icon: <EventSchedule16 />
      }
  }, [details?.micro_deposits_sent_at, details?.status])

  const breadcrumbs = useMemo(() => {
    if (hsId) {
      return [
        {
          children: 'My community',
          href: routes.myCommunity
        },
        {
          children: entityName,
          href: hospitalId
            ? `${routes.myCommunity}${routes.myOrganization}/health_systems/${hsId}/hospitals/${hospitalId}`
            : `${routes.myCommunity}${routes.myOrganization}/health_systems/${hsId}`
        },
        {
          children: bankAccountName
        }
      ]
    }
    return BREADCRUMBS(bankAccountName)
  }, [hsId, hospitalId, entityName, bankAccountName])

  if (!details) return null
  return (
    <Container
      title={bankAccountName}
      breadcrumbs={breadcrumbs}
      status={renderBankAccountStatusComponent}
      actions={actions}
      beforeStatusBlock={beforeStatusBlock}
    >
      <BankAccountDetailsBanner />
      <CustomForm<TBankAccountForm>
        form={form}
        onFinish={handleFinish}
        initialValues={initialValues}
        validateMessages={validateMessages}
        id="my-organization-create-hs-form"
        className="my-organization__create-hs__form gap-32 column"
        onValuesChange={onFormChange}
      >
        <RowLayout title="Bank account details">
          <div className="bank-account-details-grid mt-12 two-columns">
            <FieldWithLabel
              title={LABELS.BANK_NAME}
              infoTooltipText={LABELS.BANK_NAME}
            >
              {details?.bank_name}
            </FieldWithLabel>
            <FieldWithLabel
              title={LABELS.ACCOUNT_NUMBER}
              infoTooltipText={LABELS.ACCOUNT_NUMBER}
            >
              {bankAccountName}
            </FieldWithLabel>
          </div>
          {renderAccountNicknameComponent}
        </RowLayout>
        {!canChangeAccountPurpose && <Separator />}
        {renderAccountPurposeBlock}
      </CustomForm>
      <FloatFooter
        isActive={isActive}
        onCancel={onCancel}
        onSave={() => form.submit()}
      />
      <DeleteBankAccount onSuccess={onDeleteCallback} />
      <SetAsDefaultReceivableBankAccount
        onSuccess={onSetAsDefaultReceivableAccountCallback}
      />
      <VerifyDeposit onSuccess={onVerify} />
      <CantProceedWithDefaultReceivableAccount />
      <ThisAccountIsDefaultReceivableConfirmation />
      <SetNewDefaultReceivableAccount onSuccess={onDeleteCallback} />
    </Container>
  )
}
